import {useEffect, useState} from 'react'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {Audit} from '../types/audit.types'
import useAuditContext from 'app/Hooks/useAuditContext'

export const useAudits = () => {
  const {currentUser} = useAuth()
  const [audits, setAudits] = useState<Audit[]>([])
  const [selectedFileName, setSelectedFileName] = useState<{[key: string]: string}>({})
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const { currentAuditContext } = useAuditContext()

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 3000) 
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'audits')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      let Data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data()?.status,
          createdAt: doc.data().createdAt,
          auditor: doc.data().auditor, 
          audit_documents: doc.data().audit_documents,
          framework: doc.data().framework || [],
        })) as Audit[]

      Data = Data
        .filter((data) => data.status !== 'Completed')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

      if (currentUser?.role === 'Auditor') {
        Data = Data.filter((audit) => {
          if (!audit.auditor) return false
          
          if (Array.isArray(audit.auditor)) {
            const hasAccess = audit.auditor.some(auditor => 
              auditor.id === currentUser?.uid || auditor.email === currentUser?.email
            )
            return hasAccess
          } else {
            return audit.auditor.id === currentUser?.uid
          }
        })
      }
      setAudits(Data)
      setShowEmptyState(false)

      const fileNameMap = Data.reduce((acc, audit) => {
        const lastDocument = audit.audit_documents?.[audit.audit_documents.length - 1]
        if (lastDocument && lastDocument.fileName) {
          acc[audit.id] = lastDocument.fileName
        }
        return acc
      }, {} as {[key: string]: string})
      setSelectedFileName(fileNameMap)
      setIsLoading(false)
    })

    return unsubscribe
  }, [currentUser,currentAuditContext])

  return {
    audits,
    selectedFileName,
    setSelectedFileName,
    showEmptyState,
    isLoading,
  }
}